import React from 'react';

import {CollectionSVG} from '@newsela/angelou';
import {useSelector} from 'react-redux';

import {
  useNavigationBarV2Context
} from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/NavigationBar.provider';
import {
  leftContainerCss,
  libraryMenuPopoutContainer,
  newselaProductsVisibilityCss,
  rightContainerCss
} from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/ResourceMenus/ContentLibraryMenu/ContentLibraryPopout/ContentLibraryPopout.styles';
import {
  ESSENTIALS_MENU_ITEM,
  EXPLORE_CONTENT_ITEMS,
  MY_CONTENT_ITEMS,
  ProductLicenseKeysEnum,
  SUBJECT_AREA_ITEMS
} from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/shared/constants';
import {
  PopoutMenuSection
} from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/shared/PopoutMenuSection';
import {
  PopoutSectionItem
} from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/shared/PopoutSectionItem';
import {
  USER_FEATURE_FOR_PROMOTED_AT_YOUR_DISTRICT_PAGE,
  USER_FEATURE_FOR_PROMOTED_AT_YOUR_SCHOOL_PAGE,
} from 'static/three-oh/src/constants/contentPromotion';

import {getUser, getUserProperties} from '../../../../../../../modules/selectors/userSelectors';
import {useGetProductsVisibilityBreakpoint} from '../../../shared/utils';

// This component renders the global nav's "Content Library" menu pop-out
// at desktop and tablet breakpoints. It's counterpart component,
// 'HomeMenuContentLibraryPopout", renders this pop-out at mobile
// breakpoints.
const ContentLibraryPopout = () => {
  const user = useSelector((state) => getUser(state));

  const {
    getters: {
      customCollections,
      personalizedCollections,
    },
  } = useNavigationBarV2Context();

  const {
    isLiteUser,
    isStudent,
    activeELALicense,
    activeSSLicense,
    hasActivePremiumTrial,
    activeScienceLicense,
    activeEssentialsLicense,
    hasFeature,
    getExperimentSegment,
  } = useSelector((state) => getUserProperties(state));

  const visibilityBreakpoint = useGetProductsVisibilityBreakpoint();

  const showLockIconsExperiment = getExperimentSegment('show_lock_icons');

  const activeStemLicense = user?.features?.has_stem_license === 'on';

  const showLicenseItemWithLock = showLockIconsExperiment === 'on';

  const displayPromotedAtYourDistrictMenuItem = hasFeature(USER_FEATURE_FOR_PROMOTED_AT_YOUR_DISTRICT_PAGE);
  const displayPromotedAtYourSchoolMenuItem = hasFeature(USER_FEATURE_FOR_PROMOTED_AT_YOUR_SCHOOL_PAGE);

  const licensesMap = {
    [ProductLicenseKeysEnum.SS]: activeSSLicense,
    [ProductLicenseKeysEnum.ELA]: activeELALicense,
    [ProductLicenseKeysEnum.SCI]: activeScienceLicense,
    [ProductLicenseKeysEnum.STEM]: activeStemLicense,
  };

  const hideProductsSection =
    !activeELALicense &&
    !activeSSLicense &&
    !activeScienceLicense &&
    !activeEssentialsLicense &&
    !showLicenseItemWithLock;

  const isTrialOrLite = isLiteUser || hasActivePremiumTrial;

  return (
    <div className={libraryMenuPopoutContainer}>
      <div className={leftContainerCss}>
        {!hideProductsSection && (
          <div className={newselaProductsVisibilityCss(isTrialOrLite, visibilityBreakpoint)}>
            <PopoutMenuSection title="Newsela Products" width="14.375rem">
              {activeEssentialsLicense && (
                <PopoutSectionItem
                  href={ESSENTIALS_MENU_ITEM.href}
                  title={ESSENTIALS_MENU_ITEM.title}
                  SvgComponent={ESSENTIALS_MENU_ITEM.SvgComponent}
                  locked={false}
                  dataQASelector={ESSENTIALS_MENU_ITEM.dataQASelector}
                />
              )}
              {SUBJECT_AREA_ITEMS.filter(({license}) => {
                const isStem = license === ProductLicenseKeysEnum.STEM;
                const isScience = license === ProductLicenseKeysEnum.SCI;

                if (isStem) {
                  return activeStemLicense;
                }

                if (isScience) {
                  return (
                    (activeScienceLicense || (showLicenseItemWithLock && !isStudent)) &&
                    !activeStemLicense
                  );
                }

                // All other subjects
                return licensesMap[license] || (showLicenseItemWithLock && !isStudent);
              }).map(({title, href, SvgComponent, license, dataQASelector}, index) => (
                <PopoutSectionItem
                  key={index}
                  href={href}
                  title={title}
                  SvgComponent={SvgComponent}
                  locked={!licensesMap[license]}
                  dataQASelector={dataQASelector}
                />
              ))}
            </PopoutMenuSection>
          </div>
        )}

        <PopoutMenuSection title="My content" width="14.375rem">
          {MY_CONTENT_ITEMS
            .filter((item) => !isStudent || item.showForStudents)
            .filter((item) => !(item.title === 'Promoted at your district' &&
              !displayPromotedAtYourDistrictMenuItem || (item.title === 'Promoted at your school' && !displayPromotedAtYourSchoolMenuItem)))
            .map(({title, href, SvgComponent, dataQASelector, badge}) => (
              <PopoutSectionItem
                key={title}
                href={href}
                title={title}
                SvgComponent={SvgComponent}
                dataQASelector={dataQASelector}
                badge={badge}
              />
            ))}
          {personalizedCollections.map(({title, href}) => (
            <PopoutSectionItem
              key={title}
              href={href}
              title={title}
              SvgComponent={CollectionSVG}
            />
          ))}
          {customCollections.map(({title, href}) => (
            <PopoutSectionItem
              key={title}
              href={href}
              title={title}
              SvgComponent={CollectionSVG}
            />
          ))}
        </PopoutMenuSection>
      </div>

      <div className={rightContainerCss}>
        <PopoutMenuSection title="Explore content" width="14.375rem">
          {EXPLORE_CONTENT_ITEMS.filter(
            (item) =>
              (!isStudent || item.showForStudents) &&
              (!isLiteUser || item.showForLite),
          ).map(
            (
              {
                title,
                href,
                SvgComponent,
                dataQASelector,
                badge,
                openInNewTab,
              },
              index,
            ) => (
              <PopoutSectionItem
                key={index}
                href={href}
                title={title}
                badge={badge}
                openInNewTab={openInNewTab}
                SvgComponent={SvgComponent}
                dataQASelector={dataQASelector}
              />
            ),
          )}
        </PopoutMenuSection>
      </div>
    </div>
  );
};

ContentLibraryPopout.displayName = 'ContentLibraryPopout';
export {ContentLibraryPopout};
